/**
 * Warning: This code is mostly generated by AI using https://github.com/aryan02420/use-console-command as the starting point.
 */

const DEBUG_KEY = '__ly_debug__';
const HANDLERS_KEY = '__ly_debug_handlers__';

interface DebugProperties {
  QuizDetails: boolean;
  ReportEditor: boolean;
  ReportingWidgets: boolean;
  UserProfile: boolean;
}

declare global {
  interface Window {
    [DEBUG_KEY]?: DebugProperties;
    [HANDLERS_KEY]?: Set<ChangeHandler>;
  }
}

const DEFAULT_DEBUG_PROPERTIES: DebugProperties = {
  QuizDetails: false,
  ReportEditor: false,
  ReportingWidgets: false,
  UserProfile: false,
};

type ChangeHandler = (key: string, newValue: any, oldValue: any) => void;

function getChangeHandlers(): Set<ChangeHandler> {
  if (typeof window === 'undefined') {
    return new Set();
  }
  if (!window[HANDLERS_KEY]) {
    window[HANDLERS_KEY] = new Set();
  }
  return window[HANDLERS_KEY];
}

function onDebugPropertyChange(handler: ChangeHandler) {
  const handlers = getChangeHandlers();
  handlers.add(handler);
  return () => handlers.delete(handler);
}

function notifyChange(key: keyof DebugProperties, newValue: any, oldValue: any) {
  const handlers = getChangeHandlers();
  handlers.forEach((handler) => {
    try {
      handler(key, newValue, oldValue);
    } catch (error) {
      console.error('Error in debug property change handler:', error);
    }
  });
}

function createReactiveDebugObject(): DebugProperties {
  const target = { ...DEFAULT_DEBUG_PROPERTIES };

  return new Proxy(target, {
    set(obj, prop, value) {
      const oldValue = obj[prop as keyof DebugProperties];
      if (oldValue !== value) {
        obj[prop as keyof DebugProperties] = value;
        notifyChange(prop as keyof DebugProperties, value, oldValue);
      }
      return true;
    },
  });
}

function getDebugObject(): DebugProperties {
  if (typeof window === 'undefined') {
    return { ...DEFAULT_DEBUG_PROPERTIES };
  }
  if (typeof window[DEBUG_KEY] === 'undefined') {
    window[DEBUG_KEY] = createReactiveDebugObject();
  }
  return window[DEBUG_KEY]!;
}

// React Consumer Hook
import { useEffect, useState } from 'react';

export function useDebug(): DebugProperties;
export function useDebug<K extends keyof DebugProperties>(key: K): DebugProperties[K];
export function useDebug<K extends keyof DebugProperties>(key?: K) {
  const debugObj = getDebugObject();
  const [state, setState] = useState<DebugProperties | DebugProperties[K]>(() =>
    key ? debugObj[key] : { ...debugObj },
  );

  useEffect(() => {
    const unsubscribe = onDebugPropertyChange((changedKey, newValue) => {
      if (key) {
        // Single property mode
        if (changedKey === key) {
          setState(newValue);
        }
      } else {
        // Full object mode
        setState((prev) => {
          if (typeof prev === 'object' && prev !== null) {
            return { ...(prev as DebugProperties), [changedKey]: newValue };
          }
          return prev;
        });
      }
    });

    return () => {
      unsubscribe();
    };
  }, [key]);

  return state;
}

// React Provider / Initializer Component
export function DebugProvider({ children }: { children?: React.ReactNode }) {
  // Ensure the debug object is created
  getDebugObject();
  return children;
}
